import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { goBackInHistory } from '@/shell/backNavigation';
import { useHistoryReach } from './desktopHistoryReach';
import { useT } from '@/i18n/LocaleProvider';

interface HistoryNavButtonsProps {
  /** The pair's own box: the Mac app's `desktop-nav`, or the iPad header's. */
  className: string;
  /** Each button's, where the box's stylesheet does not already size them. */
  buttonClassName?: string;
  /** Each glyph's. */
  iconClassName?: string;
}

/**
 * Back and forward, browser-style: the Mac app's title bar and the iPad app's header (I-1304, IL-17)
 * draw this one pair.
 *
 * ⚠️ They move the WINDOW's history, not a tab's. Tabs in the Mac app are saved paths in one window
 * rather than separate histories, so a per-tab back would have to invent a stack the browser does
 * not have — and would disagree with the ⌘[ / ⌘] the platform already delivers.
 *
 * ⚠️ Dimmed and inert when there is nowhere to go, never hidden: a control that disappears moves the
 * one beside it, so the pair would shift under the pointer exactly when somebody is pressing it.
 */
export function HistoryNavButtons({
  className,
  buttonClassName,
  iconClassName,
}: HistoryNavButtonsProps) {
  const t = useT();
  const navigate = useNavigate();
  const { canGoBack, canGoForward } = useHistoryReach();
  return (
    <div className={className}>
      <button
        type="button"
        aria-label={t('chromeTitles.back')}
        title={t('chromeTitles.back')}
        className={buttonClassName}
        disabled={!canGoBack}
        onClick={() => goBackInHistory(navigate)}
      >
        <ChevronLeft aria-hidden="true" className={iconClassName} />
      </button>
      <button
        type="button"
        aria-label={t('navigation.desktop.forward')}
        title={t('navigation.desktop.forward')}
        className={buttonClassName}
        disabled={!canGoForward}
        onClick={() => navigate(1)}
      >
        <ChevronRight aria-hidden="true" className={iconClassName} />
      </button>
    </div>
  );
}
